import type { FinancialRef } from './initial-cases';

// #695 — THE FINANCIAL-STABILITY BAR, AS THE ANALYST'S SIDE SEES IT.
//
// "Minimum 3: 2 business + 1 banking" is one rule with three copies. It is
// contracted in apps/api/src/routes/intake.ts (#694, where it refuses a
// customer submission), mirrored in apps/portal/src/lib/validation.ts (so the
// form says it before the server does), and mirrored HERE because the staff
// side has to decide the same question about a case rather than about a
// submission. The three packages share no module — see
// docs/CROSS_PACKAGE_CONSTANTS.md — so the copy is tied to the API's source by
// a disk-read pin in financial-bar.test.ts, not by this comment.
//
// The intake copy and this one count DIFFERENT things, and the difference is
// the whole point of #695. Intake counts references SUPPLIED: three names the
// applicant nominated, which is the applicant's half of the job. This counts
// references VERIFIED: the ones 382 has actually called and written up. An
// application can satisfy the API and satisfy nothing here, which is correct —
// a nominated reference is a lead, not a finding.
export const FINANCIAL_REF_MIN_BANKING = 1;
export const FINANCIAL_REF_MIN_BUSINESS = 2;

/**
 * Has 382 verified a reference? The recorded verification is a date of contact
 * and a summary of what was said, and EITHER on its own is a verification:
 * `AddFinancialRefModal` has always let an analyst save one without the other,
 * and the staff reference list has always read a reference carrying one of
 * them as verified. Widening that here would silently un-verify references
 * recorded years ago.
 *
 * An applicant-supplied reference arrives with both empty (#545: the customer
 * says WHO to call, the analyst records what came back), so it does not count
 * until an analyst records the call — which is exactly what it is.
 */
export function isVerifiedRef(ref: FinancialRef): boolean {
  return (ref.date ?? '').trim() !== '' || (ref.summary ?? '').trim() !== '';
}

/**
 * Is the Financial Stability bar met — at least two VERIFIED business
 * references and one VERIFIED banking reference?
 *
 * Pure, and deliberately so: it is called from the reference write path to
 * decide whether the same PATCH also ticks the KYC item, and a helper that
 * reached for case state or a clock could not be driven through a boundary
 * table in a unit test.
 *
 * Types other than `business` and `banking` count for neither. `FinancialRef.type`
 * is a bare `string` on the staff side (rows predate the intake enum), so a
 * stray value has to fall somewhere; falling outside both counters keeps a
 * typo from ticking a required KYC item.
 */
export function financialBarMet(refs: readonly FinancialRef[] | undefined | null): boolean {
  if (!refs) return false;
  const verified = refs.filter(isVerifiedRef);
  const banking = verified.filter((r) => r.type === 'banking').length;
  const business = verified.filter((r) => r.type === 'business').length;
  return banking >= FINANCIAL_REF_MIN_BANKING && business >= FINANCIAL_REF_MIN_BUSINESS;
}
